import { Link } from "react-router-dom";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { SITE_URL, articleSchema } from "@/lib/seo";
import { PROJECT } from "@/constants/testIds";

const ASSETS = {
  hero: "/petalpaw-desktop-1.jpg",
  d2: "/petalpaw-desktop-2.jpg",
  d3: "/petalpaw-desktop-3.jpg",
  m1: "/petalpaw-mobile-1.png",
  m2: "/petalpaw-mobile-2.png",
  m3: "/petalpaw-mobile-3.png",
  m4: "/petalpaw-mobile-4.png",
};

function ProjectHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);

  return (
    <section data-testid={PROJECT.hero} ref={ref} className="lak-section relative h-[100svh] w-full overflow-hidden">
      <motion.div style={{ y: imageY, scale: imageScale }} className="absolute inset-0">
        <img src={ASSETS.hero} alt="Petal & Paw" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-lak-text/30" />
      <div className="absolute inset-0 flex flex-col">
        <div className="flex-1" />
        <div className="max-w-[1480px] mx-auto w-full px-6 sm:px-10 lg:px-14 pb-20 sm:pb-28 text-lak-bg">
          <Reveal><span className="eyebrow text-lak-bg/80">Case Study · 001 · 2025</span></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-8 font-serif text-6xl sm:text-7xl lg:text-9xl font-light tracking-tight leading-[0.94]">
              Petal &amp; Paw
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 font-serif italic text-2xl sm:text-3xl text-lak-bg/90 max-w-2xl">
              Pet-safe florals, beautifully online.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ProjectMeta() {
  const meta = [
    { k: "Client", v: "Petal & Paw" },
    { k: "Industry", v: "Florist · E-commerce" },
    { k: "Year", v: "2025" },
    { k: "Scope", v: "Brand-led web design, build & launch" },
  ];
  return (
    <section className="lak-section py-24 bg-lak-bg border-b border-black/10">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-2 lg:grid-cols-4 gap-12">
        {meta.map((m) => (
          <Reveal key={m.k}>
            <div>
              <span className="eyebrow">{m.k}</span>
              <p className="mt-4 font-serif text-2xl lg:text-3xl italic font-light leading-snug">{m.v}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ChallengeOutcome() {
  return (
    <section className="lak-section py-28 sm:py-40 bg-lak-bg">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-5">
          <Reveal><span className="eyebrow">The challenge</span></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.02]">
              A boutique florist, launching online for the first time.
            </h2>
          </Reveal>
        </div>
        <div className="col-span-12 lg:col-span-7 lg:pl-10 lg:border-l border-black/10">
          <Reveal delay={0.15}>
            <p className="text-lg text-lak-soft leading-relaxed">
              Petal &amp; Paw arrived with something genuinely rare, a florist offering arrangements designed
              to be safe around the animals we love. Beautiful work, a clear story, and no website yet to tell it.
            </p>
            <p className="mt-6 text-lg text-lak-soft leading-relaxed">
              Our job was to give them an online home built with the same care they put into every bouquet, calm,
              considered, unmistakably theirs, without breaking a small-business budget.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ScrollScene() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  // Pin the desktop while the mobile scrolls faster past it
  const desktopY = useTransform(scrollYProgress, [0, 1], ["20%", "-15%"]);
  const mobileY = useTransform(scrollYProgress, [0, 1], ["40%", "-90%"]);
  const captionY = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);

  return (
    <section ref={ref} className="lak-section relative bg-lak-secondary py-32 sm:py-48 overflow-hidden">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 mb-20">
        <Reveal><span className="eyebrow">Design · The build</span></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02]">
            Designed for the device <em className="italic text-lak-sage">in your hand.</em>
          </h2>
        </Reveal>
      </div>

      <div className="lak-mobile-art-grid relative max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-12 gap-6 items-start min-h-[80vh]">
        <motion.div style={{ y: desktopY }} className="col-span-12 lg:col-span-8 relative">
          <div className="bg-lak-text rounded-md p-2 sm:p-3 shadow-2xl">
            <div className="flex items-center gap-1.5 px-2 py-1.5">
              <span className="w-2 h-2 bg-white/20 rounded-full"/>
              <span className="w-2 h-2 bg-white/20 rounded-full"/>
              <span className="w-2 h-2 bg-white/20 rounded-full"/>
              <span className="ml-3 text-[10px] uppercase tracking-[0.2em] text-white/40">petalandpaw.co.uk</span>
            </div>
            <div className="overflow-hidden">
              <img src={ASSETS.d2} alt="Desktop view" className="w-full block" />
            </div>
          </div>
          <motion.p style={{ y: captionY }} className="mt-8 font-serif italic text-xl text-lak-soft max-w-md">
            “A site that feels as gentle and considered as a bouquet itself.”
          </motion.p>
        </motion.div>

        <motion.div style={{ y: mobileY }} className="col-span-6 lg:col-span-3 lg:col-start-10 z-10 lg:mt-40">
          <div className="bg-lak-text rounded-[28px] p-2 shadow-2xl border border-white/10">
            <div className="rounded-[22px] overflow-hidden aspect-[9/19]">
              <img src={ASSETS.m1} alt="Mobile hero" className="w-full h-full object-cover object-top" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function FullBleedImage({ src, caption }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.18]);
  return (
    <section ref={ref} className="lak-section relative w-full overflow-hidden bg-lak-secondary">
      <motion.div style={{ scale }} className="w-full">
        <img src={src} alt={caption} className="w-full block" />
      </motion.div>
      {caption && (
        <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 py-8">
          <p className="font-serif italic text-lg text-lak-soft">{caption}</p>
        </div>
      )}
    </section>
  );
}

function MobileTriptych() {
  return (
    <section className="lak-section py-28 sm:py-40 bg-lak-bg">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
        <Reveal><span className="eyebrow">Touch · Mobile</span></Reveal>
        <div className="grid grid-cols-12 gap-10 mt-6 mb-16 items-end">
          <div className="col-span-12 lg:col-span-7">
            <Reveal delay={0.1}>
              <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02]">
                Built mobile-first.<br /><em className="italic text-lak-sage">Refined for desktop.</em>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal delay={0.2}>
              <p className="text-lak-soft text-lg max-w-md">
                Most florist sites are an afterthought on the phone. We started there, designing every breakpoint
                with the same care, so the experience feels effortless wherever a customer finds it.
              </p>
            </Reveal>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 sm:gap-8">
          {[ASSETS.m2, ASSETS.m3, ASSETS.m4].map((src, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="bg-lak-text rounded-[24px] p-1.5 sm:p-2 shadow-xl">
                <div className="rounded-[20px] overflow-hidden aspect-[9/19]">
                  <img src={src} alt="Mobile detail" className="w-full h-full object-cover object-top" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Results() {
  const stats = [
    { k: "First-impression score", v: "+62%", sub: "Internal brand survey" },
    { k: "Mobile speed", v: "Sub-2s", sub: "Core Web Vitals · Green" },
    { k: "Enquiry conversion", v: "×3.4", sub: "vs. category benchmark" },
    { k: "Bounce rate", v: "−41%", sub: "First 30 days" },
  ];
  return (
    <section className="lak-section py-28 sm:py-40 bg-lak-secondary">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
        <Reveal><span className="eyebrow">The outcome</span></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02] max-w-4xl">
            A small studio result, <em className="italic text-lak-sage">delivered like a flagship.</em>
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px mt-16 border-y border-black/10">
          {stats.map((s, i) => (
            <Reveal key={s.k} delay={i * 0.08}>
              <div className="bg-lak-secondary p-10">
                <span className="eyebrow">{s.k}</span>
                <p className="mt-6 font-serif text-5xl sm:text-6xl font-light tracking-tighter text-lak-text">{s.v}</p>
                <p className="mt-2 text-sm text-lak-soft">{s.sub}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProjectCta() {
  return (
    <section className="lak-section py-32 sm:py-48 bg-lak-bg">
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 text-center">
        <Reveal>
          <span className="eyebrow">Your business, online, at this level</span>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-8 font-serif text-5xl sm:text-7xl lg:text-8xl font-light tracking-tight leading-[1.02]">
            Imagine a site this <em className="italic text-lak-sage">considered</em><br />
            for your brand.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <Link to="/contact" data-testid={PROJECT.cta} className="mt-14 inline-flex items-center gap-3 px-10 py-5 btn-sage text-[11px] tracking-[0.28em] uppercase">
            Start Your Project <ArrowUpRight size={16} strokeWidth={1.25} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Project() {
  const crumbs = [
    { name: "Home", url: `${SITE_URL}/` },
    { name: "Work", url: `${SITE_URL}/work` },
    { name: "Petal & Paw", url: `${SITE_URL}/work/petal-and-paw` },
  ];
  return (
    <main>
      <Seo
        title="Petal & Paw · Pet-Safe Florist Website Case Study"
        description="How LAK Atelier designed and launched an editorial e-commerce website for Petal & Paw, a London-based pet-safe florist."
        keywords="florist website design, e-commerce case study, boutique website case study, Stripe e-commerce UK"
        type="article"
        image={`${SITE_URL}/petalpaw-desktop-1.jpg`}
        breadcrumbs={crumbs}
        schemas={[articleSchema({
          title: "Petal & Paw, a pet-safe florist online",
          description: "A boutique pet-safe florist website designed and built by LAK Atelier.",
          url: `${SITE_URL}/work/petal-and-paw`,
          image: `${SITE_URL}/petalpaw-desktop-1.jpg`,
          datePublished: "2025-01-01",
        })]}
      />
      <ProjectHero />
      <ProjectMeta />
      <ChallengeOutcome />
      <FullBleedImage src={ASSETS.d3} caption="A quiet, editorial commerce experience, typography first, photography close behind." />
      <ScrollScene />
      <MobileTriptych />
      <Results />
      <ProjectCta />
    </main>
  );
}
